import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import { toast } from "react-toastify";

import { CompanySection } from "./components/CompanySection";
import { useCompanyForm } from "../hooks/everify/useCompanyForm";

import { useEverifyAddCompanies } from "../hooks/superAdminEverify/useEverifyAddCompanies";
import { useEverifyUpdateCompany } from "../hooks/superAdminEverify/useEverifyUpdateCompany";

const FIELD_SECTION_MAP = {
  name: "companyInfo",
  everifyNumber: "companyInfo",
  representativeEverifyNumber: "companyInfo",
  id: "companyInfo",

  contactPersonFirstName: "contactPerson",
  contactPersonLastName: "contactPerson",
  contactPersonPhoneNumber: "contactPerson",
  contactPersonEmail: "contactPerson",

  address: "address",
  city: "address",
  state: "address",
  zipCode: "address",
  county: "address",

  mailingAddress1: "mailingAddress",
  mailingAddress2: "mailingAddress",
  mailingCity: "mailingAddress",
  mailingState: "mailingAddress",
  mailingZip: "mailingAddress",

  employeeCount: "businessInfo",
  fein: "businessInfo",
  naicsCode: "businessInfo",
  doingBusinessAs: "businessInfo",
  dunsNumber: "businessInfo",

  notes: "additionalInfo",
  organizationCategory: "additionalInfo",
  companyUEINumber: "additionalInfo",
  whichEmployeesEVerify: "additionalInfo",
  categoryBestDescribes: "additionalInfo",
};

const SECTION_TO_ACCORDION_MAP = {
  companyInfo: "company_info",
  contactPerson: "contact_person",
  address: "address",
  mailingAddress: "mailing",
  businessInfo: "business",
  additionalInfo: "additional",
};

const REQUIRED_COMPANY_FIELDS = [
  "name",
  "address",
  "city",
  "state",
  "zipCode",
  "companyUEINumber",
  "whichEmployeesEVerify",
  "categoryBestDescribes",
];

const REQUIRED_ACCORDION_KEYS = Array.from(
  new Set(
    REQUIRED_COMPANY_FIELDS.map((field) => {
      const section = FIELD_SECTION_MAP[field];
      return SECTION_TO_ACCORDION_MAP[section];
    }).filter(Boolean)
  )
);

const DEFAULT_ACCORDION_KEYS =
  REQUIRED_ACCORDION_KEYS.length > 0
    ? [...REQUIRED_ACCORDION_KEYS]
    : ["company_info"];

const createDefaultAccordionState = () => [...DEFAULT_ACCORDION_KEYS];

const REQUIRED_FIELD_LABELS = {
  name: "Company Name",
  address: "Address",
  city: "City",
  state: "State",
  zipCode: "Zip Code",
  companyUEINumber: "UEI Number",
  whichEmployeesEVerify: "Which Employees E-Verify",
  categoryBestDescribes: "Category Best Describes",
};

const NUMERIC_FIELDS = new Set([
  "id",
  "everifyNumber",
  "representativeEverifyNumber",
  "employeeCount",
  "status",
  "companyClientID",
  "organizationCategory",
  "whichEmployeesEVerify",
  "categoryBestDescribes",
]);

const toNullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const sanitizeCompany = (company) => {
  return Object.entries(company).reduce((acc, [key, rawValue]) => {
    let value = rawValue;

    if (typeof value === "string") {
      const trimmed = value.trim();
      value = trimmed === "" ? null : trimmed;
    }

    if (NUMERIC_FIELDS.has(key)) {
      acc[key] = toNullableNumber(value);
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});
};


export const AddCompanyModal = ({ isOpen, onClose, editData }) => {
  const {
    parent,
    children,
    updateParent,
    addChildCompany,
    updateChild,
    removeChild,
    loadEditData,
    getPayload,
  } = useCompanyForm();

  const addCompanies = useEverifyAddCompanies();
  const updateCompany = useEverifyUpdateCompany();

  const [openAccordions, setOpenAccordions] = useState(
    createDefaultAccordionState
  );
  const hasInitializedChildAccordions = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setOpenAccordions(createDefaultAccordionState());
      hasInitializedChildAccordions.current = false;
    }
  }, [isOpen]);

  // Load edit data
  useEffect(() => {
    if (editData) {
      loadEditData(editData);
    }
  }, [editData]);

  useEffect(() => {
    if (!hasInitializedChildAccordions.current && children.length > 0) {
      hasInitializedChildAccordions.current = true;
      setOpenAccordions((prev) => {
        const next = new Set(prev);
        children.forEach((child) => next.add(`child_${child.id}`));
        return Array.from(next);
      });
    }
  }, [children]);

  // VALIDATION
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  };

  const validateFields = (flat, context) => {
    for (let field of REQUIRED_COMPANY_FIELDS) {
      if (!hasValue(flat[field])) {
        toast.error(
          `${context}: ${REQUIRED_FIELD_LABELS[field] || field} is required`
        );
        return false;
      }
    }
    return true;
  };

  const validateParent = () => {
    const flatParent = getPayload()[0];
    return validateFields(flatParent, "Parent company");
  };


  const validateChildren = () => {
    for (let index = 0; index < children.length; index++) {
      const child = children[index];
      const flat = {
        ...child.companyInfo,
        ...child.contactPerson,
        ...child.address,
        ...child.mailingAddress,
        ...child.businessInfo,
        ...child.additionalInfo,
      };

      const childName = child.companyInfo?.name?.trim();
      const childLabel = childName
        ? `Child company "${childName}"`
        : `Child company ${index + 1}`;

      if (!validateFields(flat, childLabel)) {
        return false;
      }
    }
    return true;
  };


  const handleAddChildCompany = () => {
    const child = addChildCompany();
    if (child?.id) {
      setOpenAccordions((prev) => {
        const next = new Set(prev);
        next.add(`child_${child.id}`);
        return Array.from(next);
      });
    }
  };

  // SUBMIT
  const handleSubmit = async () => {
    if (!validateParent()) return;
    if (!validateChildren()) return;

    let payload = getPayload();

    payload = payload.map((company, index) => {
      const sanitized = sanitizeCompany(company);
      const desiredStatus =
        editData && sanitized.status != null ? sanitized.status : 1;
      return {
        ...sanitized,
        status: desiredStatus,
        companyClientID: sanitized.companyClientID ?? 1,
        isParent: index === 0, // first = parent, rest = child
      };
    });

    const id = toast.loading("Saving company...");

    try {
      if (editData) {
        await updateCompany.mutateAsync([payload[0]]);
      } else {
        await addCompanies.mutateAsync(payload);
      }

      toast.update(id, {
        render: editData ? "Company updated!" : "Company created!",
        type: "success",
        isLoading: false,
        autoClose: 1200,
      });

      onClose();
    } catch (err) {
      console.error(err);
      toast.update(id, {
        render: "Error occurred",
        type: "error",
        isLoading: false,
      });
    }
  };


  // RENDER
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
          {editData ? "Edit Company" : "Add Company"}
        </Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {/* PARENT */}
        <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1 }}>
          Parent Company
        </Typography>

        {/* PARENT SECTIONS */}
        <CompanySection
          sectionKey="company_info"
          title="Company Info"
          sectionData={parent.companyInfo}
          updateFn={(field, value) =>
            updateParent("companyInfo", field, value)
          }
          requiredFields={REQUIRED_COMPANY_FIELDS}
          openAccordions={openAccordions}
          setOpenAccordions={setOpenAccordions}
        />

        <CompanySection
          sectionKey="contact_person"
          title="Contact Person"
          sectionData={parent.contactPerson}
          updateFn={(field, value) =>
            updateParent("contactPerson", field, value)
          }
          requiredFields={REQUIRED_COMPANY_FIELDS}
          openAccordions={openAccordions}
          setOpenAccordions={setOpenAccordions}
        />

        <CompanySection
          sectionKey="address"
          title="Address"
          sectionData={parent.address}
          updateFn={(field, value) => updateParent("address", field, value)}
          requiredFields={REQUIRED_COMPANY_FIELDS}
          openAccordions={openAccordions}
          setOpenAccordions={setOpenAccordions}
        />

        <CompanySection
          sectionKey="mailing"
          title="Mailing Address"
          sectionData={parent.mailingAddress}
          updateFn={(field, value) =>
            updateParent("mailingAddress", field, value)
          }
          requiredFields={REQUIRED_COMPANY_FIELDS}
          openAccordions={openAccordions}
          setOpenAccordions={setOpenAccordions}
        />

        <CompanySection
          sectionKey="business"
          title="Business Info"
          sectionData={parent.businessInfo}
          updateFn={(field, value) =>
            updateParent("businessInfo", field, value)
          }
          requiredFields={REQUIRED_COMPANY_FIELDS}
          openAccordions={openAccordions}
          setOpenAccordions={setOpenAccordions}
        />

        <CompanySection
          sectionKey="additional"
          title="Additional Info"
          sectionData={parent.additionalInfo}
          updateFn={(field, value) =>
            updateParent("additionalInfo", field, value)
          }
          requiredFields={REQUIRED_COMPANY_FIELDS}
          openAccordions={openAccordions}
          setOpenAccordions={setOpenAccordions}
        />

        {/* CHILD COMPANIES */}
        <Box
          sx={{
            mt: 5,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
            Child Companies
          </Typography>

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddChildCompany}
            sx={{
              backgroundColor: "#2563EB",
              color: "#fff",
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
          >
            Add Child Company
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {children.map((child) => (
            <CompanySection
              key={child.id}
              sectionKey={`child_${child.id}`}
              title="Child Company"
              sectionData={{
                ...child.companyInfo,
                ...child.contactPerson,
                ...child.address,
                ...child.mailingAddress,
                ...child.businessInfo,
                ...child.additionalInfo,
              }}
              updateFn={(field, value) =>
                updateChild(child.id, resolveSection(field), field, value)
              }
              requiredFields={REQUIRED_COMPANY_FIELDS}
              childrenMode
              onDelete={() => removeChild(child.id)}
              openAccordions={openAccordions}
              setOpenAccordions={setOpenAccordions}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            backgroundColor: "#F7D154",
            color: "#111",
            borderRadius: "999px",
            px: 4,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          {editData ? "Update Company" : "Save Company"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// reverses flat field to section name
function resolveSection(field) {
  return FIELD_SECTION_MAP[field];
}

export default AddCompanyModal;
